import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { FaUser, FaEnvelope, FaLock, FaArrowLeft } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    gender: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGenderSelect = (gender) => {
    setFormData({ ...formData, gender });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.gender) {
      toast.error('Por favor selecciona tu género');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        gender: formData.gender
      });
      
      if (response.data.success) {
        toast.success(formData.gender === 'male' ? '¡Bienvenido, Guerrero!' : '¡Bienvenida, Guerrera!');
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al crear la cuenta';
      toast.error(errorMsg);
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-6 py-12 relative"
      style={{ backgroundColor: '#0a0a0a' }}
    >
      {/* Mesh gradient background */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 70% 30%, rgba(0, 240, 255, 0.15), transparent 50%), radial-gradient(circle at 30% 70%, rgba(var(--brand-primary-glow), 0.1), transparent 40%)'
        }}
      />
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          className="mb-8 text-gray-400 hover:text-white"
        >
          <FaArrowLeft className="mr-2" /> Volver al inicio
        </Button>

        {/* Logo */}
        <div className="text-center mb-8">
          <h1 
            className="text-4xl font-bold text-white mb-3"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            DeisyCFit
          </h1>
          <p className="text-gray-400">Crea tu cuenta y comienza tu transformación</p>
        </div>

        {/* Register Card */}
        <div 
          className="rounded-3xl p-8 backdrop-blur-xl"
          style={{ 
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <h2 
            className="text-2xl font-bold text-white mb-6"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Crear Cuenta
          </h2>
          
          <form onSubmit={handleRegister} className="space-y-5">
            {/* Nombre */}
            <div>
              <Label className="text-gray-300 text-sm font-medium mb-2 block">Nombre completo</Label>
              <div className="relative">
                <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Tu nombre"
                  required
                  className="w-full pl-11 py-6 rounded-xl bg-black/50 border-white/10 text-white placeholder:text-gray-500 focus:border-[var(--brand-primary)]"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <Label className="text-gray-300 text-sm font-medium mb-2 block">Email</Label>
              <div className="relative">
                <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="tu@email.com"
                  required
                  className="w-full pl-11 py-6 rounded-xl bg-black/50 border-white/10 text-white placeholder:text-gray-500 focus:border-[var(--brand-primary)]"
                />
              </div>
            </div>
            
            {/* Género */}
            <div>
              <Label className="text-gray-300 text-sm font-medium mb-3 block">¿Cómo te identificas?</Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleGenderSelect('female')}
                  className={`p-4 rounded-2xl border-2 transition-all ${
                    formData.gender === 'female'
                      ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 scale-105'
                      : 'border-white/10 hover:border-[var(--brand-primary)]/50 bg-black/30'
                  }`}
                  style={formData.gender === 'female' ? { boxShadow: '0 0 20px rgba(var(--brand-primary-glow), 0.3)' } : {}}
                >
                  <div className="text-4xl mb-2">👩</div>
                  <div className={`font-bold ${formData.gender === 'female' ? 'text-[var(--brand-primary)]' : 'text-white'}`}>
                    Guerrera
                  </div>
                  <div className="text-xs text-gray-500">Mujer</div>
                </button>
                
                <button
                  type="button"
                  onClick={() => handleGenderSelect('male')}
                  className={`p-4 rounded-2xl border-2 transition-all ${
                    formData.gender === 'male'
                      ? 'border-[#00F0FF] bg-[#00F0FF]/10 scale-105'
                      : 'border-white/10 hover:border-[#00F0FF]/50 bg-black/30'
                  }`}
                  style={formData.gender === 'male' ? { boxShadow: '0 0 20px rgba(0, 240, 255, 0.3)' } : {}}
                >
                  <div className="text-4xl mb-2">👨</div>
                  <div className={`font-bold ${formData.gender === 'male' ? 'text-[#00F0FF]' : 'text-white'}`}>
                    Guerrero
                  </div>
                  <div className="text-xs text-gray-500">Hombre</div>
                </button>
              </div>
            </div>

            {/* Contraseña */}
            <div>
              <Label className="text-gray-300 text-sm font-medium mb-2 block">Contraseña</Label>
              <div className="relative">
                <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  className="w-full pl-11 py-6 rounded-xl bg-black/50 border-white/10 text-white placeholder:text-gray-500 focus:border-[var(--brand-primary)]"
                />
              </div>
            </div>

            {/* Confirmar Contraseña */}
            <div>
              <Label className="text-gray-300 text-sm font-medium mb-2 block">Confirmar Contraseña</Label>
              <div className="relative">
                <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  className="w-full pl-11 py-6 rounded-xl bg-black/50 border-white/10 text-white placeholder:text-gray-500 focus:border-[var(--brand-primary)]"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full py-6 rounded-full font-bold text-white uppercase tracking-wider transition-all"
              style={{ 
                background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))',
              }}
              disabled={loading}
            >
              {loading ? 'Creando cuenta...' : 'Crear Cuenta'}
            </Button>
          </form>
        </div>

        {/* Login Link */}
        <div className="mt-8 text-center">
          <p className="text-gray-400">
            ¿Ya tienes cuenta?{' '}
            <button 
              onClick={() => navigate('/login')}
              className="font-semibold hover:underline"
              style={{ color: 'var(--brand-primary)' }}
            >
              Inicia sesión aquí
            </button>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Register;
